import { jsPDF } from 'jspdf'

import { AudienceParams, MetricaItem } from '@/pdf/types'
import { renderHtmlToImage, getLogoDataUrl } from '@/pdf/htmlRenderer'

function escapeHtml(str: unknown): string {
    const s = String(str ?? '')
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

// ─── Card builder ─────────────────────────────────────────────────────────────

interface CardData { title: string; value: string; description: string }

function metricaToCard(
    item: MetricaItem | undefined,
    title: string,
    description: string,
    preferFormatted = false,
): CardData | null {
    if (!item) return null
    let value: string
    if (preferFormatted && item.valor_atual_formatado) {
        value = item.valor_atual_formatado
    } else if (item.valor_atual != null) {
        value = item.valor_atual.toLocaleString('pt-BR')
    } else {
        return null
    }
    return { title, value, description }
}

function cardHtml(card: CardData): string {
    const digits = card.value.replace(/\D/g, '').length
    const valueFontSize = digits > 7 ? 18 : digits > 5 ? 22 : 28
    return `
    <div style="background:white;border-radius:6px;padding:14px 14px 14px 20px;position:relative;box-shadow:1.5px 2px 0 #d7d7de;min-height:132px;display:flex;flex-direction:column;justify-content:center;">
        <div style="position:absolute;left:0;top:0;bottom:0;width:6px;background:#2e5eaa;border-radius:6px 0 0 6px;"></div>
        <p style="font-size:11px;color:#252525;margin:0 0 8px;font-weight:normal;">${escapeHtml(card.title)}</p>
        <p style="font-size:${valueFontSize}px;color:#2e5eaa;margin:0 0 8px;font-weight:bold;">${escapeHtml(card.value)}</p>
        <p style="font-size:10px;color:#6e6e6e;margin:0;line-height:1.4;">${escapeHtml(card.description)}</p>
    </div>`
}

// ─── HTML page builder ────────────────────────────────────────────────────────

function buildMetricsHtml(parametros: AudienceParams, logoSrc: string): string {
    const agora = new Date()
    const dataGerado = agora.toLocaleDateString('pt-BR')
    const horaGerado = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    const nomeRadio = parametros.companyName || ''
    const mp = parametros.metricas_principais
    const re = parametros.resumo_executivo

    // Metric cards
    const cards: CardData[] = []
    if (mp) {
        const c1 = metricaToCard(mp.total_sessoes, 'Total de Sessões', 'Número total de acessos de ouvintes únicos no período', true)
        if (c1) cards.push(c1)

        const c2 = metricaToCard(mp.pico_audiencia, 'Pico de Audiência', 'Número máximo de ouvintes conectados ao streaming simultaneamente.', true)
        if (c2) cards.push(c2)

        if (mp.media_ouvintes_simultaneos?.valor_atual !== undefined) {
            cards.push({
                title: 'Média de Ouvintes',
                value: `${mp.media_ouvintes_simultaneos.valor_atual.toLocaleString('pt-BR')} ouvintes`,
                description: 'Quantidade média de ouvintes conectados ao mesmo tempo',
            })
        }

        if (mp.tempo_medio_por_sessao) {
            cards.push({
                title: 'Tempo médio de audiência',
                value: mp.tempo_medio_por_sessao.valor_atual_formatado || `${mp.tempo_medio_por_sessao.valor_atual_minutos ?? 0}m`,
                description: 'É a média de tempo que cada ouvinte passa conectado ao streaming',
            })
        }

        if (mp.tempo_total_audicao) {
            cards.push({
                title: 'Tempo total de audição',
                value: mp.tempo_total_audicao.valor_atual_formatado || `${Math.floor(mp.tempo_total_audicao.valor_atual_horas ?? 0)}h`,
                description: 'É a soma de todo o tempo que os ouvintes passaram conectados ao streaming',
            })
        }
    }

    const cardsHtml = cards.map(cardHtml).join('')

    // Insights section
    const insightText = parametros.insight_unificado_metricas_principais || re?.frase_destaque
    let insightsHtml = ''
    if (insightText) {
        insightsHtml += `
        <div style="margin-top:28px; position:relative;">
            <div style="background:#e5eaf5;border-radius:5px;padding:10px 14px 10px 18px;position:relative;">
                <!-- barra lateral -->
                <div style="position:absolute;left:0;top:0;bottom:0;width:6px;background:#2e5eaa;border-radius:5px 0 0 5px;"></div>
                
                <!-- título dentro da div -->
                <span style="
                display:inline-block;
                font-size:13.6px;
                font-weight:bold;
                color:#2e5eaa;
                margin-bottom:10px;
                ">
                    Insights do Período
                </span>

                <p style="font-size:12px;color:#32405a;font-style:italic;margin:0;line-height:1.5;">
                    ${escapeHtml(insightText)}
                </p>
            </div>
        </div>`
            }

    // Alerts section
    if (re?.alertas && re.alertas.length > 0) {
        const alertItems = re.alertas
            .map(a => `<p style="font-size:12px;color:#781e1e;margin:2px 0;line-height:1.4;">• ${escapeHtml(a)}</p>`)
            .join('')
        insightsHtml += `
        <div style="background:#ffebeb;border-radius:5px;padding:10px 14px 12px 18px;position:relative;margin-top:16px;">
            <div style="position:absolute;left:0;top:0;bottom:0;width:6px;background:#c83c3c;border-radius:5px 0 0 5px;"></div>
            <p style="font-size:12px;font-weight:bold;color:#aa2828;margin:0 0 6px;">Alertas detectados</p>
            ${alertItems}
        </div>`
    }

    return `
    <div style="width:794px;height:1122px;background:#fafafa;font-family:Arial,sans-serif;padding:60px 57px 40px;box-sizing:border-box;position:relative;">
        ${logoSrc ? `<img src="${logoSrc}" style="position:absolute;top:20px;right:57px;height:22px;" />` : ''}
        <h1 style="font-size:26px;font-weight:bold;color:#2e5eaa;margin:0 0 8px;">Audiência Digital</h1>
        <p style="font-size:14px;color:#6e6e6e;margin:0 0 24px;">Métricas de Audiência</p>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
            ${cardsHtml}
        </div>

        ${insightsHtml}

        <div style="position:absolute;bottom:24px;left:57px;right:57px;text-align:center;">
            <span style="font-size:9px;color:#a0a0a0;">Relat&oacute;rio gerado em ${dataGerado} &agrave;s ${horaGerado}${nomeRadio ? ` - ${nomeRadio}` : ''}</span>
        </div>
    </div>`
}

// ─── Page entry point ─────────────────────────────────────────────────────────

export async function addMetricsPage(pdf: jsPDF, parametros: AudienceParams): Promise<void> {
    const logoSrc = await getLogoDataUrl()
    const img = await renderHtmlToImage(buildMetricsHtml(parametros, logoSrc))
    pdf.addPage()
    pdf.addImage(img, 'JPEG', 0, 0, 210, 297)
}



